import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Form, Card, Row, Col, Select, Input, 
    Button, message, Affix, Space } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useLazyQuery } from '@apollo/client';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import {  Routes } from '../../routes';
import { useParam, useCreateEditLocales } from '../../hooks'; 
import { DEFAULT_LOCALE_CODE } from '../../const'; 
import { useDeleteCategoryMutation, useCreateCategoryMutation,
    usePatchCategoryMutation } from '../../gql/hooks/mutations';
import { CATEGORY_QUERY } from '../../gql/queries';
import { useLocalesQuery } from '../../gql/hooks';
import { LanguageSelector } from '../../components/formItems';
import RichTextEditor from '../../components/controls/RichTextEditor/RichTextEditor'; 
import { Section } from '../../components/section/Section';
import DeleteButton from '../../components/DeleteButton';
import { Loadable } from '../../components/loadable/Loadable';
import { ErrorPopup } from '../../components/errorPopup/ErrorPopup';
import ListSearchItemsByIds from '../../updated/components/ListSearchItemsByIds/ListSearchItemsByIds';
import { useSegmentation } from '../../hooks/useSegmentation'; 
import useBrands from '../../updated/gql/hooks/useBrands'; 
import './styles.less';

const CreateEditCategoryView = ( prop ) => { 
    const [ courseIds, setCourseIds ] = useState();
    const [ curriculumIds, setCurriculumIds ] = useState();
    const [ formController ] = Form.useForm();
    const [ isEditing, setIsEditing ] = useState( false ); 
    const [ selectedBrand, setSelectedBrand ] = useState( null );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ editSortOrder, setEditSortOrder ] = useState( null );
    const [ categoryObject, setCategoryObject ] = useState(); 
    const { brands, brandsLoading } = useBrands();
    const [ requireBrands ] = useState( true );

    let initialFormValues = {
        name: '',
        description: '',
        brand: selectedBrand,
        locale: DEFAULT_LOCALE_CODE
    };    

    const { loading: segmentationLoading, addInitialValues, initializeSegmentation, 
        saveSegmentation, renderSegmentation } = useSegmentation( formController, { updatedBrandsLocation: true } );
    
    initialFormValues = addInitialValues( initialFormValues );

    const { data: allLocales, error: localesError } = useLocalesQuery( { fetchPolicy: 'network-only' } );
 
     
    const [ currentLocaleCode, setLocales, descriptionRawHTML, handleChangeLocale, setCurrentLocaleCode, validateEnglishExists ] = useCreateEditLocales( formController );
    
    const parseLocales = useCallback( ( newLocales ) => {
        if ( !allLocales ) {
            return [];
        }
        return allLocales.locales.edges.map( ( { node } ) => {
            const found = newLocales ? newLocales.find( c => c.code === node.code ) : false;
            return found 
                ? { locale: found.code, name: found.name || '', description: found.description || '' } 
                : { locale: node.code, name: '', description: '', isNew: true };
        } ); 
    }, [ allLocales ] );

    // Check for the categoryId in the route
    const { value: paramCategoryId, checked: checkedRouteForId } = useParam( 'categoryId' );
  
    // Set the categoryId, either from the route or if that doesn't exist generate a new one  
     
    const categoryId = useMemo( () => { 
        if ( checkedRouteForId ) { 
            if ( paramCategoryId ) {  
                setIsEditing( true ); 
                formController.setFields( [ { name: 'id', value: paramCategoryId } ] ); 
                return paramCategoryId;
            } else {
                setIsEditing( false ); 
                setLocales( parseLocales() );
                return null;
            }
        } else {
            return undefined;
        }
    }, [ checkedRouteForId, paramCategoryId, formController, setLocales, parseLocales ] );

    const [ fetchCategory, { data: categoryData, loading: categoryLoading, 
        error: categoryError } ] = useLazyQuery( CATEGORY_QUERY, {  fetchPolicy: 'network-only' }  );
     
    // Fetches course if isEditing
    useEffect( () => {
        if ( isEditing && categoryId  ) {
            fetchCategory( { variables: { categoryID: categoryId } } );
        } 
    }, [ fetchCategory, isEditing, categoryId  ] );

    useEffect( () => {
        if ( !categoryData || !categoryId ) { return ; }
        
        const selCategory = categoryData.category.node;
        setCategoryObject( selCategory );

        if ( selCategory.brand && selCategory.brand !== null ) {
            setSelectedBrand( selCategory.brand.id );
            formController.setFields( [ { name: 'brand', value: selCategory.brand.id } ] );
            formController.setFields( [ { name: 'brandId', value: selCategory.brand.id } ] );
        }
        setEditSortOrder( selCategory.sortOrder );

        const locales = parseLocales( selCategory.locales );
        setLocales( locales );

        setCourseIds( selCategory.courseIds || '' );
        setCurriculumIds( selCategory.curriculumIds || '' );

        /*
         * Set the default locale to en-US IF there is a name value for en-US.
         * If there is not a value find the first locale that does.
         */
        let usaLocale = locales.find( i => i.locale === 'en-US' );
        if ( !usaLocale ) {
            usaLocale = locales.find( i => i.name !== '' );  
        }

        if ( usaLocale ) {
            formController.setFields( [ { name: 'locale', value: usaLocale.locale } ] ); 
            setCurrentLocaleCode( usaLocale.locale );
        } else {
            formController.setFields( [ { name: 'locale', value: 'en-US' } ] ); 
            setCurrentLocaleCode( 'en-US' );
        }
    }, [ categoryData, categoryId, formController, setLocales, 
        setSelectedBrand, setCurrentLocaleCode, parseLocales ] );

    /**
     * Form Submission, patch, create   
     */

    const [ createCategory, { loading: createLoading, 
        error: createError } ] = useCreateCategoryMutation();     
    const [ patchCategory, { loading: patchLoading,  
        error: patchError } ] = usePatchCategoryMutation();

    /**
     * If any of the queries are executing isLoading = true
     */
    const isLoading = useMemo( () => {
        return  categoryLoading || createLoading || patchLoading || brandsLoading;
    }, [ categoryLoading, createLoading, patchLoading, brandsLoading  ] );

    const handleFormSubmission = async ( formValues ) => {
        setIsSaving( true );

        // Trigger change locale so it saves the latest changes from the text inputs
        const formLocales = handleChangeLocale( currentLocaleCode );
        
        // // Filter locals so we only use the ones that have changed
        // const filteredLocals = formLocales.filter( i => {  
        //     return !i.isNew || ( i.name !== '' && i.description !== '' );
        // } )
        const filteredLocals = formLocales.filter( i => {  
            return !( i.name === '' && i.description === '' );
        } ).filter( i => !( i.name === null && i.description === null ) )
        // Change 'locale' to 'code' 
        .map( i => {
            return {
                name: i.name,
                description: i.description,
                code: i.locale
            };
        } ); 
        /*
         * Get the en-US name to use as the category default.
         * If en-US is blank use the first locale with a name value.
         */
        const usLocale = filteredLocals.find( i => i.code === 'en-US' );
        const catName = usLocale && usLocale.name !== '' 
            ? usLocale.name
            : filteredLocals.find( i => i.name !== '' ).name;
        
        // Create the input for the mutation
        const input = {
            id: formController.getFieldValue( 'id' ),
            locales: filteredLocals,
            name: catName
        }; 
        
        if ( isEditing ) {
            input.sortOrder = editSortOrder;

            if ( requireBrands ) {
                input.brandId = formController.getFieldValue( 'brandId' );
            } else {
                input.brandId = selectedBrand;
            }
        } else {
            input.brandId = formController.getFieldValue( 'brandId' );
        }

        try {
            // Save the new category data.
            if ( isEditing ) {
                await patchCategory( { variables: { input } } );
                await saveSegmentation( formValues, formController.getFieldValue( 'id' ), 'category' );
            } else {
                const response = await createCategory( { variables: { input } } ); 
                await saveSegmentation( formValues, response?.data?.result?.id, 'category' );
            }

            // Handle completion.
            if ( !( patchError || createError ) ) {
                setTimeout( () => {
                    message.success( 'Category Saved' );
                    window.history.back();
                    setIsSaving( false );
                }, 8000 );
            } else {
                message.error( 'Error saving category' );
                setIsSaving( false ); 
            }
        // eslint-disable-next-line no-unused-vars
        } catch ( e ) {
            message.error( 'Error saving category' );
            setIsSaving( false );    
        }
    };

    useEffect( () => {
        if ( categoryObject && !segmentationLoading ) {
            initializeSegmentation( categoryObject );
        }
    }, [ categoryObject, segmentationLoading ] );

    /**
     * Delete Category handling
     */

    const [ deleteItem ] = useDeleteCategoryMutation();

    const handleDelete = useCallback( async () => {
        const input = {
            brandId: selectedBrand, 
            id: categoryId 
        };
        await deleteItem( { variables: { input } } );
        window.history.back();
    }, [ selectedBrand, deleteItem, categoryId ] );
 
    return (
        <Layout className="create-edit-category-view">
            <Layout>         
                { /* Breadcrumbs */ }
                <PageHeader
                    title= { isEditing ? 'Edit Category' : 'Create Category' }
                    onBack={ () => window.history.back() }        
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                        { breadcrumbName: 'Content Taxonomy', path: Routes.ContentTaxonomyNav.buildPath() },
                        { breadcrumbName: 'Categories', path: Routes.ListCategoriesView.buildPath() },
                        { breadcrumbName: isEditing ? 'Edit' : 'Create' },
                    ] ) } 
                />

                { /* Category Form */ }
                <ErrorPopup errors={ [ categoryError, localesError ] } />

                <Loadable loading={ isLoading || isSaving }>
                    <Section> 
                        <Form   
                            form={ formController }
                            name="category-form"
                            layout="vertical"
                            initialValues={ 
                                { ...initialFormValues, brand: selectedBrand }
                            }
                            onFinish={ handleFormSubmission }
                            scrollToFirstError
                        >
                            <Row gutter={ [ 24, 24 ] }>
                                <Col md={ 24 }>
                                    <Card title="General Settings">
                                        <Row gutter={ [ 24, 24 ] }>
                                            <Col xs={ 24 } lg={ 18 }>
    
                                                { /* Locale chooser */ }                                                
                                                <Form.Item 
                                                    label="Language"
                                                    name="locale"
                                                    rules={ [
                                                        {
                                                            message: 'You need to add a title and description for English (United States)',
                                                            validateTrigger: [],
                                                            validator: ( _, ) => {
                                                                if ( currentLocaleCode === DEFAULT_LOCALE_CODE ) {
                                                                    return Promise.resolve();
                                                                }
                                                                    
                                                                if ( validateEnglishExists() ) {
                                                                    return Promise.resolve();
                                                                } else {
                                                                    return Promise.reject();
                                                                }
                                                            }
                                                        }
                                                    ] }
                                                >
                                                    <LanguageSelector
                                                        mode="select-single"
                                                        onChange={ ( code ) => handleChangeLocale( code ) }
                                                    />
                                                </Form.Item>

                                                { /* Name input */ }
                                                <Form.Item
                                                    name="name"
                                                    label="Category Name"
                                                    maxLength= { 64 }
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'Catagories are required to have a name.',
                                                        },
                                                        {
                                                            max: 64,
                                                            type: 'string',
                                                            message: 'Category names can only be 64 characters long.',
                                                        }
                                                    ] }
                                                >
                                                    <Input 
                                                        placeholder="Category Name" 
                                                    />
                                                </Form.Item>

                                                { /* Description input */ }

                                                <Form.Item
                                                    name="description"
                                                    label="Category Description"
                                                    
                                                >
                                                    <RichTextEditor 
                                                        controls="simple"  
                                                        locale={ currentLocaleCode }
                                                        rawHTML={ descriptionRawHTML }
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        
                                        { requireBrands && 
                                            <Form.Item
                                                name="brandId"
                                                label="Brand"
                                                style={ { maxWidth: '75%' } }
                                                rules={ [
                                                    {
                                                        required: true,
                                                        message: 'A brand is required for categories.',
                                                    } ] 
                                                }
                                            >
                                                <Select
                                                    mode="single" 
                                                    placeholder="Select Brand"  
                                                    filterOption={ ( input, option ) => 
                                                        ( option?.children ? 
                                                            option.children[ 0 ].type ? '' : option.children[ 0 ]
                                                            : '' ).toLowerCase().includes( input.toLowerCase() )
                                                    }
                                                >
                                                    {
                                                        brands.map( i => {
                                                            return ( 
                                                                <Select.Option 
                                                                    key={ i.id } 
                                                                    value={ i.id }  
                                                                >
                                                                    { i.name || <span className='italic'>No Name</span> }
                                                                </Select.Option>
                                                            );
                                                        } )
                                                    }
                                                </Select>
                                            </Form.Item>
                                        }

                                        <br/>

                                        { courseIds?.length > 0 &&
                                            <ListSearchItemsByIds 
                                                title="Courses in this Category"
                                                ids={ courseIds }
                                            />
                                        }

                                        { curriculumIds?.length > 0 &&
                                            <ListSearchItemsByIds 
                                                ids={ curriculumIds }
                                                title="Curricula in this Category"
                                                type="curricula"
                                            />
                                        }
                                    </Card>  
                                    { renderSegmentation( isLoading ) }
                                </Col>
                            </Row>
                        </Form>         
                    </Section>
                </Loadable>
            </Layout>  

            { /* Right column buttons */ }
            <Layout.Sider className="side-action-bar" width={ 100 }>
                <Affix  offsetTop={ 98 }>
                    <Space direction="vertical" size={ 16 }>
                        { /* Submit button */ }
                        <Button
                            type="primary"
                            disabled={ isSaving || isLoading }
                            htmlType="submit" 
                            onClick={ () => {
                                formController.submit();
                            } }
                        >
                            Save
                        </Button>
                        
                        { /* Cancel button */ }
                        <Button 
                            disabled={ isSaving || isLoading }
                            onClick={ () => window.history.back() }
                        >
                            Cancel
                        </Button>
                        
                        { /* Delete button */ }

                        { categoryId && 
                            <DeleteButton
                                danger
                                type="link"
                                disabled={ isSaving || isLoading }
                                itemType="category"  
                                onDelete={ handleDelete }
                                superSafe={ false }
                                confirmMessage= { 
                                    <React.Fragment>
                                        <p>Are you <i>sure</i> you want to permanently delete this category? 
                                        All users who have progress in this item will <i>lose their progress</i>. This action cannot be undone.</p>
                                    </React.Fragment>
                                }
                            >
                                Delete
                            </DeleteButton>
                        }
                    </Space>
                </Affix>
            </Layout.Sider>
        </Layout>
    );
};

export default CreateEditCategoryView;
